import { HosLogType, IInspectionItemData, IInspectionPart, IInspectionSectionData, IInspectionTemplateDetails } from "../store/types";

export const UNKNOWN_ITEM_STATUS = "Unknown";

/**
 * Status submitted when the driver flags an item as a defect. The defect lifecycle starts at
 * "Reported". Drive submits "Reported" for defects; the server rejects the legacy "Fail" status.
 */
export const DEFECT_ITEM_STATUS = "Reported";

/**
 * API-facing shape for an inspected item sent during submission.
 * Maps to the InspectedItem entity accepted by SaveInspectedSection.
 *
 * Only carries values Drive itself produces; the server rejects the legacy "Fail" status, so this type does not model it.
 */
export interface IApiInspectedItem {
    inspectionItem: { id: string };
    itemStatus: "Pass" | "Reported" | "Unknown";
    itemNote?: string;
    itemSeverity?: "Major" | "Minor" | "Cosmetic";
    itemValue?: string;
    mediaFiles?: { id: string }[];
}

const INSPECTION_TYPE_TO_LOG_TYPE: Record<HosLogType, string> = {
    PreTrip: "PreTrip",
    PostTrip: "PostTrip",
    MidTrip: "InTrip",
    Other: "Other"
};

const LOG_TYPE_TO_INSPECTION_TYPE: Record<string, HosLogType> = {
    PreTrip: "PreTrip",
    PostTrip: "PostTrip",
    InTrip: "MidTrip",
    Other: "Other"
};

/**
 * Maps Drive UI inspection type to the API InspectionLogType.
 * "MidTrip" → "InTrip" to match backend enum.
 */
export function mapInspectionTypeToLogType(inspectionType: HosLogType): string {
    return INSPECTION_TYPE_TO_LOG_TYPE[inspectionType];
}

/**
 * Builds an IApiInspectedItem for any non-OtherItem (CheckItem, CustomProperty, SignatureItem, or
 * any future itemType the backend introduces). Returns Reported, Pass, or Unknown based on
 * hasDefect/noDefects.
 */
function mapDraftNonOtherItem(itemId: string, itemType: string, itemData: IInspectionItemData | undefined, noDefects: boolean, isRegulated: boolean): IApiInspectedItem {
    if (itemData?.hasDefect === true) {
        const apiItem: IApiInspectedItem = { inspectionItem: { id: itemId }, itemStatus: DEFECT_ITEM_STATUS };
        if (itemData.notes) { apiItem.itemNote = itemData.notes; }
        if (isRegulated && itemData.severity) { apiItem.itemSeverity = itemData.severity as "Major" | "Minor" | "Cosmetic"; }
        if (itemType === "CustomProperty" && itemData.value) { apiItem.itemValue = itemData.value.trim(); }
        const mediaIds = (itemData.images ?? []).filter(Boolean);
        if (mediaIds.length > 0) { apiItem.mediaFiles = mediaIds.map(id => ({ id })); }
        return apiItem;
    }
    if (itemData?.hasDefect === false || noDefects) {
        const passItem: IApiInspectedItem = { inspectionItem: { id: itemId }, itemStatus: "Pass" };
        if (itemData?.notes) { passItem.itemNote = itemData.notes; }
        if (itemType === "CustomProperty" && itemData?.value) { passItem.itemValue = itemData.value.trim(); }
        const mediaIds = (itemData?.images ?? []).filter(Boolean);
        if (mediaIds.length > 0) { passItem.mediaFiles = mediaIds.map(id => ({ id })); }
        return passItem;
    }
    // hasDefect is undefined: item not yet evaluated → Unknown so resume can
    // distinguish "not touched" from "confirmed passing".
    const unknownItem: IApiInspectedItem = { inspectionItem: { id: itemId }, itemStatus: UNKNOWN_ITEM_STATUS };
    if (itemData?.notes) { unknownItem.itemNote = itemData.notes; }
    if (itemType === "CustomProperty" && itemData?.value) { unknownItem.itemValue = itemData.value.trim(); }
    return unknownItem;
}

function mapOtherItem(itemId: string, itemData: IInspectionItemData | undefined, isRegulated: boolean): IApiInspectedItem | null {
    const value = itemData?.value?.trim();
    if (!value) { return null; }
    const apiItem: IApiInspectedItem = { inspectionItem: { id: itemId }, itemStatus: DEFECT_ITEM_STATUS, itemValue: value };
    // Not covered by unit tests: itemData is always defined here (a non-empty value was read from it), so the optional chains below never short-circuit. This code requires rework the next time it is changed.
    if (itemData?.notes) { apiItem.itemNote = itemData.notes; }
    if (isRegulated && itemData?.severity) { apiItem.itemSeverity = itemData.severity as "Major" | "Minor" | "Cosmetic"; }
    const mediaIds = (itemData?.images ?? []).filter(Boolean);
    if (mediaIds.length > 0) { apiItem.mediaFiles = mediaIds.map(id => ({ id })); }
    return apiItem;
}

/**
 * Maps API InspectionLogType back to Drive UI inspection type.
 * "InTrip" → "MidTrip" to match frontend enum.
 */
export function mapLogTypeToInspectionType(logType: string): HosLogType | undefined {
    return LOG_TYPE_TO_INSPECTION_TYPE[logType];
}

/**
 * Transforms all section data from the UI model to an array of API InspectedItems, in template order.
 * @param excludeItemIds - Template-level item IDs to skip entirely (e.g. carry-forward defects
 *   that are linked separately via LinkExistingInspectedItems and must not be re-created).
 * @param skipItemIds - Additional item IDs to omit (e.g. custom properties not applicable to
 *   the current asset type — these should neither be saved nor sent to the backend).
 */
export function mapSectionDataToInspectedItems(
    sectionDataMap: Record<string, IInspectionSectionData>,
    templateDetails: IInspectionTemplateDetails,
    excludeItemIds?: Set<string>,
    skipItemIds?: Set<string>
): IApiInspectedItem[] {
    const result: IApiInspectedItem[] = [];

    for (const part of templateDetails.inspectionParts ?? []) {
        const sectionData = sectionDataMap[part.id];

        for (const item of part.inspectionItems ?? []) {
            if (excludeItemIds?.has(item.id)) {
                continue;
            }
            if (skipItemIds?.has(item.id)) {
                continue;
            }

            const itemData = sectionData?.items[item.id];

            if (item.itemType === "OtherItem") {
                const otherItem = mapOtherItem(item.id, itemData, item.isRegulated !== false);
                if (otherItem) { result.push(otherItem); }
                continue;
            }

            const hasDefect = itemData?.hasDefect ?? false;

            const apiItem: IApiInspectedItem = {
                inspectionItem: { id: item.id },
                itemStatus: hasDefect ? DEFECT_ITEM_STATUS : "Pass"
            };

            if (itemData?.notes) {
                apiItem.itemNote = itemData.notes;
            }

            // Not covered by unit tests: hasDefect is only true when itemData is defined, so the optional chain never short-circuits. This code requires rework the next time it is changed.
            if (hasDefect && item.isRegulated !== false && itemData?.severity) {
                apiItem.itemSeverity = itemData.severity as "Major" | "Minor" | "Cosmetic";
            }

            if (item.itemType === "CustomProperty" && itemData?.value) {
                apiItem.itemValue = itemData.value.trim();
            }

            const uploadedMediaIds = (itemData?.images ?? []).filter(Boolean);
            if (uploadedMediaIds.length > 0) {
                apiItem.mediaFiles = uploadedMediaIds.map(id => ({ id }));
            }

            result.push(apiItem);
        }
    }

    return result;
}

/**
 * Transforms section data into InspectedItems for a Draft save.
 * Unlike the submit mapper, only sections in sectionDataMap are included.
 * Items the user hasn't evaluated are sent as Unknown so that on resume,
 * loadDraftSectionData can distinguish "confirmed passing" from "not yet touched".
 * @param excludeItemIds - Template-level item IDs to skip (carry-forward defects that will be
 *   linked separately via linkExistingInspectedItems on final submission and must not be
 *   written into the draft — otherwise they would be pre-filled but unlocked on reopen).
 * @param skipItemIds - Additional item IDs to omit (e.g. custom properties not applicable to
 *   the current asset type — these should neither be saved nor sent to the backend).
 */
export function mapSectionDataToDraftInspectedItems(
    sectionDataMap: Record<string, IInspectionSectionData>,
    templateDetails: IInspectionTemplateDetails,
    excludeItemIds?: Set<string>,
    skipItemIds?: Set<string>
): IApiInspectedItem[] {
    const result: IApiInspectedItem[] = [];

    for (const part of templateDetails.inspectionParts ?? []) {
        const sectionData = sectionDataMap[part.id];
        if (!sectionData) {
            continue;
        }

        for (const item of part.inspectionItems ?? []) {
            if (excludeItemIds?.has(item.id)) {
                continue;
            }
            if (skipItemIds?.has(item.id)) {
                continue;
            }

            const itemData = sectionData.items[item.id];

            // OtherItem is always either a defect (has text) or absent (no text). There is no Unknown state
            // for OtherItem — if the driver clears the field after a draft save, the item will be absent
            // on resume. This is acceptable because OtherItem is always optional.
            // Also skipped when noDefects is set, consistent with mapDraftNonOtherItem, which treats a
            // noDefects section's items as Pass. Otherwise an OtherItem text entry would draft as a
            // defect while every CheckItem drafts as Pass.
            if (item.itemType === "OtherItem") {
                const otherItem = sectionData.noDefects ? null : mapOtherItem(item.id, itemData, item.isRegulated !== false);
                if (otherItem) { result.push(otherItem); }
                continue;
            }

            result.push(mapDraftNonOtherItem(item.id, item.itemType, itemData, sectionData.noDefects, item.isRegulated !== false));
        }
    }

    return result;
}

/**
 * Filters a flat array of API InspectedItems to only those belonging to the given section.
 */
export function getInspectedItemsForSection(allItems: IApiInspectedItem[], section: IInspectionPart): IApiInspectedItem[] {
    const sectionItemIds = new Set((section.inspectionItems ?? []).map(item => item.id));
    return allItems.filter(item => sectionItemIds.has(item.inspectionItem.id));
}
